import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup } from '../_fitnessHelpers';
import { CNetActions } from '../other/_cnet';

/* -------------------- BMI HORIZONTAL COLOR BAR (matches Calculator.net) -------------------- */
function BMIGauge({ bmi }) {
  const bands = [
    { from: 0,    to: 18.5, color: '#3b82f6', label: 'Underweight' },
    { from: 18.5, to: 25,   color: '#22c55e', label: 'Normal' },
    { from: 25,   to: 30,   color: '#eab308', label: 'Overweight' },
    { from: 30,   to: 35,   color: '#f97316', label: 'Obese' },
    { from: 35,   to: 40,   color: '#ef4444', label: 'Extremely obese' },
  ];
  const min = 12, max = 40;
  const clamp = Math.max(min, Math.min(max, bmi));
  const xFor = v => 40 + ((v - min) / (max - min)) * 340;
  const arrowX = xFor(clamp);
  const catForBmi = (b) => bands.find(x => b >= x.from && b < x.to)?.label || 'Extremely obese';
  const currentCat = catForBmi(bmi);
  return (
    <div className="my-3">
      <svg viewBox="0 0 400 130" className="w-full max-w-lg mx-auto">
        {/* Arrow + BMI badge above the bar */}
        <g transform={`translate(${arrowX} 0)`}>
          <rect x="-32" y="10" width="64" height="26" rx="6" fill="#0f172a"/>
          <text x="0" y="28" fontSize="13" fontWeight="700" fill="#ffffff" textAnchor="middle">{bmi.toFixed(1)}</text>
          <polygon points="0,50 -7,40 7,40" fill="#0f172a"/>
        </g>
        {/* Colour bands */}
        {bands.map((b, i) => (
          <rect key={i} x={xFor(b.from)} y={55} width={xFor(b.to) - xFor(b.from)} height={26} fill={b.color}/>
        ))}
        {/* Boundary ticks + numbers */}
        {[12, 18.5, 25, 30, 35, 40].map(v => (
          <g key={v}>
            <line x1={xFor(v)} y1={81} x2={xFor(v)} y2={88} stroke="#334155" strokeWidth="1"/>
            <text x={xFor(v)} y={100} fontSize="10" fill="#334155" textAnchor="middle">{v}</text>
          </g>
        ))}
        {/* Category labels below */}
        {bands.map((b, i) => (
          <text key={i} x={(xFor(b.from) + xFor(b.to))/2} y={120} fontSize="10" fill="#475569" textAnchor="middle" fontWeight="600">{b.label}</text>
        ))}
      </svg>
      <div className="text-center mt-1 text-xl font-bold text-slate-900">BMI = <span style={{ color: bmi < 18.5 || bmi >= 25 ? '#c86870' : '#2f9c3f' }}>{bmi.toFixed(1)}</span> <span className="text-sm font-medium text-slate-600">({currentCat})</span></div>
    </div>
  );
}

export default function BMICalc() {
  const [tab, setTab] = useState(0);
  const [age, setAge] = useState(25);
  const [gender, setGender] = useState('male');
  const [ft, setFt] = useState(5);
  const [inches, setInches] = useState(10);
  const [lb, setLb] = useState(160);
  const [cm, setCm] = useState(178);
  const [kg, setKg] = useState(73);
  const [showResult, setShowResult] = useState(true);
  const doClear = () => { setAge(25); setGender('male'); setFt(5); setInches(10); setLb(160); setCm(178); setKg(73); setShowResult(false); };
  const doCalc = () => setShowResult(true);

  const c = useMemo(() => {
    let heightM = 0, weightKg = 0;
    if (tab === 0) {
      const totIn = (+ft || 0) * 12 + (+inches || 0);
      heightM = totIn * 0.0254;
      weightKg = (+lb || 0) * 0.453592;
    } else {
      heightM = (+cm || 0) / 100;
      weightKg = +kg || 0;
    }
    const bmi = heightM > 0 ? weightKg / (heightM * heightM) : 0;
    let cat = 'Normal', color = '#2f9c3f';
    if (bmi < 16) { cat = 'Severe Thinness'; color = '#c8536b'; }
    else if (bmi < 17) { cat = 'Moderate Thinness'; color = '#c8536b'; }
    else if (bmi < 18.5) { cat = 'Mild Thinness'; color = '#dc8ea1'; }
    else if (bmi < 25) { cat = 'Normal'; color = '#2f9c3f'; }
    else if (bmi < 30) { cat = 'Overweight'; color = '#e39024'; }
    else if (bmi < 35) { cat = 'Obese Class I'; color = '#c86870'; }
    else if (bmi < 40) { cat = 'Obese Class II'; color = '#a63946'; }
    else { cat = 'Obese Class III'; color = '#8b1e2f'; }

    const healthyMin = 18.5 * heightM * heightM;
    const healthyMax = 25 * heightM * heightM;
    const bmiPrime = bmi / 25;
    const ponderal = heightM > 0 ? weightKg / (heightM ** 3) : 0;
    const disp = (val, dp = 1) => tab === 0 ? `${(val / 0.453592).toFixed(dp)} lbs` : `${val.toFixed(dp)} kg`;
    return { bmi, cat, color, heightM, weightKg, healthyMin, healthyMax, bmiPrime, ponderal, disp };
  }, [tab, ft, inches, lb, cm, kg]);

  const heightInputs = tab === 0 ? (
    <div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={ft} inches={inches} onFt={setFt} onIn={setInches} testidPrefix="bmi-h" /></div>
  ) : (
    <UnitInput value={cm} onChange={setCm} unit="cm" testid="bmi-h-cm" />
  );
  const weightInputs = tab === 0 ? (
    <UnitInput value={lb} onChange={setLb} unit="pounds" testid="bmi-w-lb" />
  ) : (
    <UnitInput value={kg} onChange={setKg} unit="kg" testid="bmi-w-kg" />
  );

  const bmiTable = (
    <div className="calc-card p-6">
      <h3 className="font-bold text-slate-900 mb-3">BMI Table for Adults (World Health Organization)</h3>
      <table className="w-full text-sm border-collapse">
        <thead><tr>
          <th className="bg-[#1e40af] text-white px-3 py-2 text-left border border-slate-300">Category</th>
          <th className="bg-[#1e40af] text-white px-3 py-2 text-left border border-slate-300">BMI range – kg/m²</th>
        </tr></thead>
        <tbody>
          <tr><td className="border px-3 py-1.5">Severe Thinness</td><td className="border px-3 py-1.5">&lt; 16</td></tr>
          <tr className="bg-slate-50"><td className="border px-3 py-1.5">Moderate Thinness</td><td className="border px-3 py-1.5">16 – 17</td></tr>
          <tr><td className="border px-3 py-1.5">Mild Thinness</td><td className="border px-3 py-1.5">17 – 18.5</td></tr>
          <tr className="bg-slate-50"><td className="border px-3 py-1.5">Normal</td><td className="border px-3 py-1.5">18.5 – 25</td></tr>
          <tr><td className="border px-3 py-1.5">Overweight</td><td className="border px-3 py-1.5">25 – 30</td></tr>
          <tr className="bg-slate-50"><td className="border px-3 py-1.5">Obese Class I</td><td className="border px-3 py-1.5">30 – 35</td></tr>
          <tr><td className="border px-3 py-1.5">Obese Class II</td><td className="border px-3 py-1.5">35 – 40</td></tr>
          <tr className="bg-slate-50"><td className="border px-3 py-1.5">Obese Class III</td><td className="border px-3 py-1.5">&gt; 40</td></tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <CalcShell showActions={false} look="cnet" title="BMI Calculator" subtitle="Body Mass Index — screening measure of body fat based on height and weight.">
      <CalcNetPanel
        slug="bmi"
        activeTab={tab}
        onTabChange={setTab}
        belowGrid={showResult && bmiTable}
        right={ showResult ? (
          <div>
            <div className="text-xl md:text-2xl font-bold text-slate-900">
              BMI = <span style={{ color: c.color }}>{c.bmi.toFixed(1)}</span> kg/m² (<span style={{ color: c.color }}>{c.cat}</span>)
            </div>
            <BMIGauge bmi={c.bmi} />
            <ul className="text-sm text-slate-800 space-y-1.5 mt-3 list-disc pl-5" data-testid="bmi-info-list">
              <li>Healthy BMI range: <b>18.5 kg/m² – 25 kg/m²</b></li>
              <li>Healthy weight for the height: <b>{c.disp(c.healthyMin)} – {c.disp(c.healthyMax)}</b></li>
              <li>BMI Prime: <b>{c.bmiPrime.toFixed(2)}</b></li>
              <li>Ponderal Index: <b>{c.ponderal.toFixed(1)} kg/m³</b></li>
            </ul>
          </div>
        ) : <div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see your BMI.</div>
        }
      >
        <Row label="Age"><UnitInput value={age} onChange={setAge} testid="bmi-age" /><span className="text-slate-500 text-xs whitespace-nowrap">ages: 2 – 120</span></Row>
        <Row label="Gender">
          <RadioGroup name="bmi-gender" value={gender} onChange={setGender}
            options={[{value: 'male', label: 'Male'}, {value: 'female', label: 'Female'}]} />
        </Row>
        <Row label="Height">{heightInputs}</Row>
        <Row label="Weight">{weightInputs}</Row>
        <CNetActions onCalc={doCalc} onClear={doClear} testId="bmi" />
      </CalcNetPanel>

      <div data-inputs-end />

      {showResult && <StepsPanel steps={[
        `Convert height and weight to metric — height = ${c.heightM.toFixed(3)} m, weight = ${c.weightKg.toFixed(2)} kg`,
        `BMI = weight (kg) / height (m)² = ${c.weightKg.toFixed(2)} / (${c.heightM.toFixed(2)})² = <b>${c.bmi.toFixed(2)}</b>`,
        `Category: <b>${c.cat}</b>. Healthy weight range for height: ${c.disp(c.healthyMin)} – ${c.disp(c.healthyMax)}.`
      ]} />}
    </CalcShell>
  );
}
